/**
 * Entry point — mounts React and provides app-wide context.
 *
 *   StrictMode
 *     GoogleOAuthProvider     (only when a client id is configured)
 *       QueryClientProvider   server data (React Query)
 *         CollegeProvider     which college's store we're viewing
 *           CustomerAuthProvider   the signed-in student (Google login)
 *             CartProvider     the shopping cart
 *               App (router) + CollegePicker
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { CollegeProvider } from '@/context/CollegeContext';
import { CustomerAuthProvider } from '@/context/CustomerAuthContext';
import { CartProvider } from '@/context/CartContext';
import { CollegePicker } from '@/components/CollegePicker';
import App from './App.jsx';
import './index.css';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

const tree = (
  <QueryClientProvider client={queryClient}>
    <CollegeProvider>
      <CustomerAuthProvider>
        <CartProvider>
          <App />
          <CollegePicker />
        </CartProvider>
      </CustomerAuthProvider>
    </CollegeProvider>
  </QueryClientProvider>
);

createRoot(document.getElementById('root')).render(
  <StrictMode>
    {/* GoogleOAuthProvider needs a client id; when it's absent we still render
        the app (login just shows a "not configured" note). */}
    {GOOGLE_CLIENT_ID ? (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{tree}</GoogleOAuthProvider>
    ) : (
      tree
    )}
  </StrictMode>,
);
